import React, { Component, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
} from '@mui/material';
import { CaptivePortalTemplateProps } from '../types';
import { CaptivePortalContainer, isCaptivePortalFormDataValid } from './container';

export const ModernTemplateDataKeys = [
  'logoUrl',
  'header',
  'content',
  'buttonText',
  'showName',
  'showEmail',
  'requireName',
  'requireEmail',
  'requireAcceptMarketing',
  'termsAndConditionsUrl',
  'privacyPolicyUrl',
  'primaryColor',
  'buttonColor',
  'buttonTextColor',
  'cardBackgroundColor',
  'cardFieldTextColor',
  'cardFieldBorderColor',
  'headerTextColor',
  'contentTextColor',
  'cardTextColor',
] as const;

export const ModernTemplate: React.FC<
  CaptivePortalTemplateProps<(typeof ModernTemplateDataKeys)[number]>
> = (props) => {
  const { data, formData, onFormChange, onSubmit, isSubmitting = false } = props;
  const { t } = useTranslation();

  const handleInputChange =
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onFormChange({
        ...formData,
        [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
      });
    };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCaptivePortalFormDataValid(data, formData)) {
      onSubmit();
    }
  };

  return (
    <CaptivePortalContainer {...props}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {data.logoUrl && (
              <Box sx={{ mb: 3 }}>
                <Box
                  component="img"
                  src={data.logoUrl}
                  alt={t('logo', 'Logo')}
                  sx={{ height: 80, maxWidth: '100%' }}
                />
              </Box>
            )}

            {data.header && (
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  color: data.headerTextColor || 'text.primary',
                }}
              >
                {data.header}
              </Typography>
            )}

            {data.content && (
              <Typography
                variant="h6"
                sx={{
                  color: data.contentTextColor || 'text.secondary',
                  maxWidth: 'md',
                  mx: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {data.content}
              </Typography>
            )}
          </Box>

          <Paper
            elevation={8}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.100',
              backgroundColor: data.cardBackgroundColor || 'background.paper',
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Email Field */}
                {data.showEmail && (
                  <StyledTextField
                    data={data}
                    type="email"
                    label={t('emailAddress', 'Email Address')}
                    value={formData.email || ''}
                    onChange={handleInputChange('email')}
                    placeholder={t('emailPlaceholder', 'your@email.com')}
                    fullWidth
                    variant="outlined"
                    required={data.requireEmail}
                  />
                )}

                {/* Name Fields */}
                {data.showName && (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <StyledTextField
                        data={data}
                        type="text"
                        label={t('firstName', 'First Name')}
                        value={formData.firstName || ''}
                        onChange={handleInputChange('firstName')}
                        placeholder={t('firstNamePlaceholder', 'John')}
                        fullWidth
                        variant="outlined"
                        required={data.requireName}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <StyledTextField
                        data={data}
                        type="text"
                        label={t('lastName', 'Last Name')}
                        value={formData.lastName || ''}
                        onChange={handleInputChange('lastName')}
                        placeholder={t('lastNamePlaceholder', 'Doe')}
                        fullWidth
                        variant="outlined"
                        required={data.requireName}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>

              {(data.termsAndConditionsUrl || data.requireAcceptMarketing) && (
                <Box display="flex" flexDirection="column">
                  {data.termsAndConditionsUrl && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.acceptedTerms || false}
                          onChange={handleInputChange('acceptedTerms')}
                          sx={{
                            color: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
                            '&.Mui-checked': {
                              color:
                                data.cardFieldBorderColor || data.primaryColor || 'primary.main',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ color: data.cardTextColor || 'text.primary' }}
                        >
                          {t('acceptTerms', 'I accept the')}{' '}
                          <Link
                            href={data.termsAndConditionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: data.primaryColor || 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {t('termsConditions', 'Terms & Conditions')}
                          </Link>
                          {data.privacyPolicyUrl && (
                            <>
                              {' '}
                              {t('and', 'and')}{' '}
                              <Link
                                href={data.privacyPolicyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: data.primaryColor || 'primary.main',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {t('privacyPolicy', 'Privacy Policy')}
                              </Link>
                            </>
                          )}
                          .
                        </Typography>
                      }
                      sx={{ alignItems: 'center', mb: 0.5 }}
                    />
                  )}

                  {data.requireAcceptMarketing && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.acceptedMarketing || false}
                          onChange={handleInputChange('acceptedMarketing')}
                          sx={{
                            color: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
                            '&.Mui-checked': {
                              color:
                                data.cardFieldBorderColor || data.primaryColor || 'primary.main',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ color: data.cardTextColor || 'text.primary' }}
                        >
                          {t(
                            'acceptMarketing',
                            'I would like to receive marketing communications and promotional offers',
                          )}
                        </Typography>
                      }
                      sx={{ alignItems: 'center' }}
                    />
                  )}
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || !isCaptivePortalFormDataValid(data, formData)}
                fullWidth
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'semibold',
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: data.buttonColor || '#4f46e5',
                  color: data.buttonTextColor || '#ffffff',
                  '&:hover': {
                    backgroundColor: data.buttonColor || '#4338ca',
                    transform: 'scale(1.02)',
                    boxShadow: 4,
                  },
                  '&:disabled': {
                    backgroundColor: data.buttonColor || '#4f46e5',
                    color: data.buttonTextColor || '#ffffff',
                    opacity: 0.7,
                    transform: 'none',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    {t('connecting', 'Connecting...')}
                  </Box>
                ) : (
                  data.buttonText || t('getConnected', 'Get Connected')
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </CaptivePortalContainer>
  );
};

function StyledTextField(
  props: ComponentProps<typeof TextField> & {
    data: CaptivePortalTemplateProps<(typeof ModernTemplateDataKeys)[number]>['data'];
  },
) {
  const { data } = props;
  return (
    <TextField
      {...props}
      sx={{
        '& .MuiInputLabel-root': {
          color: data.cardFieldTextColor || 'text.primary',
        },
        '& .MuiInputBase-input': {
          color: data.cardFieldTextColor || 'text.primary',
        },
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover fieldset': {
            borderColor: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-focused fieldset': {
            borderColor: data.buttonColor || data.primaryColor || 'primary.main',
          },
        },
      }}
    />
  );
}
